import { Mono } from '@jotpsych/ui';
import { Box, Divider, Stack, Typography } from '@mui/material';

import type { ClaimWorkItem } from '../../../store';
import type { ClaimProblemSummary } from '../../../types';

import { type ClaimWorkPayerStatus, formatPayerStatusLine } from './payerStatusLine';
import { buildReasonDetail } from './reasonDetailModel';

export type ClaimWorkOutcomeReason = NonNullable<ClaimWorkItem['outcome_reason']>;

export interface ReasonDetailProps {
  reason?: ClaimWorkOutcomeReason | null;
  payerStatus?: ClaimWorkPayerStatus | null;
  problem?: ClaimProblemSummary | null;
  loading?: boolean;
  error?: boolean;
}

const TEXT_SX = { fontSize: 'inherit', lineHeight: 1.5, overflowWrap: 'anywhere' } as const;
const LABEL_SX = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
} as const;

export const ReasonDetail = ({
  reason,
  payerStatus,
  problem,
  loading,
  error,
}: ReasonDetailProps) => {
  const model = buildReasonDetail(reason, problem);
  const payerLine = formatPayerStatusLine(payerStatus ? { ...payerStatus, code: null } : null);
  return (
    <Stack spacing={1.5} sx={{ maxHeight: 'min(480px, 65vh)', overflowY: 'auto' }}>
      <Box>
        <Typography color="text.secondary" sx={LABEL_SX}>
          Payer response
        </Typography>
        <Typography
          component="div"
          sx={{ ...TEXT_SX, fontSize: '0.9375rem', fontWeight: 700, mt: 0.5 }}
        >
          {model.title}
        </Typography>
        {(payerLine || model.source) && (
          <Typography
            color="text.secondary"
            component="div"
            sx={{ ...TEXT_SX, fontSize: '0.75rem', mt: 0.25 }}
          >
            {[payerLine, model.source].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {model.payerSummary && model.payerSummary !== model.title && (
          <Typography sx={{ ...TEXT_SX, mt: 1 }}>{model.payerSummary}</Typography>
        )}
      </Box>
      {model.summary && (
        <Box
          sx={{
            borderLeft: '2px solid',
            borderColor: 'primary.main',
            pl: 1.25,
          }}
        >
          <Typography color="primary.main" sx={LABEL_SX}>
            Explanation
          </Typography>
          <Typography sx={{ ...TEXT_SX, mt: 0.5 }}>{model.summary}</Typography>
        </Box>
      )}
      {model.nextStep && (
        <Box>
          <Typography color="text.secondary" sx={LABEL_SX}>
            {model.nextStepIsAi ? 'Suggested next step' : 'Next step'}
          </Typography>
          <Typography sx={{ ...TEXT_SX, mt: 0.5 }}>{model.nextStep}</Typography>
        </Box>
      )}
      {model.patientBillingNote && (
        <Typography sx={{ ...TEXT_SX, bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
          {model.patientBillingNote}
        </Typography>
      )}
      {(model.codes.length > 0 || model.sourceReasons.length > 0) && (
        <>
          <Divider />
          <Box>
            <Typography color="text.secondary" sx={LABEL_SX}>
              Payer codes
            </Typography>
            {model.sourceReasons.length > 0 ? (
              <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, mt: 0.75, p: 0 }}>
                {model.sourceReasons.map((entry, index) => (
                  <Box component="li" key={entry.code ?? index}>
                    <Typography component="div" sx={{ ...TEXT_SX, fontWeight: 600 }}>
                      {entry.code && (
                        <Mono component="span" sx={{ fontSize: 'inherit' }}>
                          {entry.code}
                        </Mono>
                      )}
                      {entry.code && entry.title ? ' · ' : ''}
                      {entry.title}
                    </Typography>
                    {entry.description && (
                      <Typography color="text.secondary" sx={TEXT_SX}>
                        {entry.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Mono component="div" sx={{ ...TEXT_SX, mt: 0.75 }}>
                {model.codes.join(' · ')}
              </Mono>
            )}
            {model.sourceReasons.length > 0 && model.additionalCodes.length > 0 && (
              <Typography color="text.secondary" component="div" sx={{ ...TEXT_SX, mt: 1 }}>
                Also reported:{' '}
                <Mono component="span" sx={{ fontSize: 'inherit' }}>
                  {model.additionalCodes.join(' · ')}
                </Mono>
              </Typography>
            )}
          </Box>
        </>
      )}
      {loading ? (
        <Typography color="text.secondary" role="status" sx={TEXT_SX}>
          Loading explanation…
        </Typography>
      ) : (
        !problem && (
          <Typography color="text.secondary" sx={TEXT_SX}>
            {error
              ? 'The explanation could not be loaded. Open the claim to review its payer response.'
              : 'No detailed explanation is available yet. Open the claim to review its payer response.'}
          </Typography>
        )
      )}
    </Stack>
  );
};
